(() => {
  'use strict';
  const GLOBAL_COOKIE = 'xteink_selector_global';
  const HANDOFF_COOKIE = 'xteink_selector_handoff';
  const HANDOFF_TTL = 180000;
  const hostKey = (hostname) => hostname.toLowerCase().replace(/^www\./, '');

  function normalizeDestination(config, target, source) {
    const url = new URL(target, source);
    for (const [siteId, site] of Object.entries(config.sites)) {
      const destination = new URL(site.origin);
      const canonical = hostKey(url.hostname) === hostKey(destination.hostname);
      const alias = url.protocol === 'https:' && !url.port && site.aliases?.includes(url.hostname.toLowerCase());
      if (!canonical && !alias) continue;
      if (alias) url.host = destination.host;
      for (const choice of mappingsFor(config).filter((option) => option.site === siteId)) {
        const prefix = choice.pathAliases?.find((path) => url.pathname === path || url.pathname.startsWith(`${path}/`));
        if (prefix) {
          url.pathname = `${choice.path.replace(/\/$/, '')}${url.pathname.slice(prefix.length)}` || '/';
          break;
        }
      }
      break;
    }
    return url.href;
  }

  function validateHandoff(config, value, hostname, now = Date.now()) {
    if (!value || value.version !== 1 || typeof value.id !== 'string' ||
        !/^[A-Z]{2}$/.test(value.country || '') || !Number.isFinite(value.expires) ||
        value.expires <= now || value.expires > now + HANDOFF_TTL ||
        typeof value.attempted !== 'boolean') return null;
    const choice = mappingsFor(config).find((item) => item.id === value.id);
    if (!choice || choice.languageCode !== value.language ||
        (choice.countryCode !== null && choice.countryCode !== value.country)) return null;
    try {
      const destination = new URL(config.sites[choice.site].origin);
      const source = new URL(value.source);
      if (value.target !== hostKey(destination.hostname) || hostKey(destination.hostname) !== hostKey(hostname) ||
          hostKey(source.hostname) === hostKey(destination.hostname) ||
          !Object.values(config.sites).some((site) => hostKey(new URL(site.origin).hostname) === hostKey(source.hostname)) ||
          !value.returnTo?.startsWith('/') || value.returnTo.startsWith('//') || /[\r\n\\]/.test(value.returnTo)) return null;
      const target = new URL(value.returnTo, destination);
      const prefix = choice.path.replace(/\/$/, '');
      if (target.origin !== destination.origin || (prefix && target.pathname !== prefix && !target.pathname.startsWith(`${prefix}/`))) return null;
      return { ...value, choice };
    } catch { return null; }
  }

  function createHandoff(config, choice, target, source, now = Date.now()) {
    try {
      const url = new URL(normalizeDestination(config, target, source));
      if (hostKey(url.hostname) === hostKey(new URL(source).hostname)) return null;
      const value = { version: 1, id: choice.id, country: choice.countryCode || choice.actualCountry,
        language: choice.languageCode, source: new URL(source).origin, target: hostKey(url.hostname),
        returnTo: `${url.pathname}${url.search}${url.hash}`, expires: now + HANDOFF_TTL, attempted: false };
      return validateHandoff(config, value, url.hostname, now) ? value : null;
    } catch { return null; }
  }

  function mappingsFor(config) {
    return config.regions.flatMap((region) => region.options.map((option) => ({ ...option, region: region.id })));
  }

  function preserveQuery(target, source) {
    const url = new URL(target, source);
    const original = new URL(source);
    const query = original.search.slice(1).split('&').filter((part) => {
      let key;
      try { key = decodeURIComponent(part.split('=')[0]).toLowerCase(); } catch { return true; }
      return !['country', 'oc', 'mdapp_countrycodedomain'].includes(key);
    }).join('&');
    url.search = query;
    url.hash = original.hash;
    return url.href;
  }

  function globalTarget(config, source) {
    const original = new URL(source);
    const fallback = mappingsFor(config).find((option) => option.id === config.fallbackId);
    const prefixes = [...new Set(mappingsFor(config).flatMap((option) => [option.path, ...(option.pathAliases || [])]).filter((path) => path !== '/'))]
      .sort((a, b) => b.length - a.length);
    const prefix = prefixes.find((path) => original.pathname === path || original.pathname.startsWith(`${path}/`));
    const resource = prefix ? original.pathname.slice(prefix.length) || '/' : original.pathname;
    const origin = config.sites[fallback.site].origin;
    const base = fallback.path === '/' ? '' : fallback.path.replace(/\/$/, '');
    return preserveQuery(new URL(`${base}${resource}`, origin).href, source);
  }

  function labelFor(config, country, language, global = false, format, languageName) {
    const options = mappingsFor(config);
    const candidates = options.filter((option) => option.countryCode === country);
    const matchesLanguage = (item) => item.languageCode === language || language?.startsWith(`${item.languageCode}-`);
    const option = !global && (candidates.find(matchesLanguage) || candidates[0]) ||
      options.find((item) => item.id === config.fallbackId);
    // A country's configured default is a destination, not the language of this page.
    const actualLanguageName = language?.toLowerCase().split('-')[0] === 'pt'
      ? 'Português' : languageName || options.find(matchesLanguage)?.languageName || language;
    return format === 'parentheses' ? `${option.countryName} (${actualLanguageName})` : `${option.countryName} / ${actualLanguageName}`;
  }

  function createNavigation(win, doc) {
    const labels = doc.getElementById('CountryLanguageSelector')?.dataset;
    if (!labels) return null;
    let configPromise;
    let config;
    let selected;
    let virtual;
    let geoFallback = false;
    let redirectBinding;
    let destroyed = false;
    let suspended = false;
    let historyGeneration = 0;
    let bindTimer;
    let automaticPending = false;
    let handoffPending = false;
    let handoffTimer;
    const cookieValue = (name) => doc.cookie.split('; ').find((part) => part.startsWith(`${name}=`))?.slice(name.length + 1);
    let incoming;
    try { incoming = JSON.parse(decodeURIComponent(cookieValue(HANDOFF_COOKIE) || '')); } catch { /* No transfer. */ }
    // Hold plugin auto-navigation while the confirmed cross-store selection is applied.
    handoffPending = Boolean(incoming && incoming.target === hostKey(win.location.hostname) && incoming.expires > Date.now());
    const writeHandoff = (value) => {
      const domain = /(^|\.)xteink\.com$/.test(win.location.hostname) ? '; Domain=xteink.com' : '';
      const encoded = value ? encodeURIComponent(JSON.stringify(value)) : '';
      if (encoded.length > 3800) throw new Error('Cross-store selection exceeds cookie capacity');
      doc.cookie = `${HANDOFF_COOKIE}=${encoded}; Path=/; Max-Age=${value ? Math.max(1, Math.ceil((value.expires - Date.now()) / 1000)) : 0}; SameSite=Lax${win.location.protocol === 'https:' ? '; Secure' : ''}${domain}`;
      if (value && cookieValue(HANDOFF_COOKIE) !== encoded) throw new Error('Cross-store selection could not be saved');
    };
    const readGlobal = () => {
      try {
        const raw = doc.cookie.split('; ').find((cookie) => cookie.startsWith(`${GLOBAL_COOKIE}=`));
        const country = raw && decodeURIComponent(raw.split('=')[1]);
        return /^[A-Z]{2}$/.test(country || '') ? country : null;
      } catch { return null; }
    };
    const saveGlobal = (country) => {
      const domain = /(^|\.)xteink\.com$/.test(win.location.hostname) ? '; Domain=xteink.com' : '';
      doc.cookie = `${GLOBAL_COOKIE}=${country || ''}; Path=/; Max-Age=${country ? 5184000 : 0}; SameSite=Lax${win.location.protocol === 'https:' ? '; Secure' : ''}${domain}`;
    };
    async function ready() {
      if (!configPromise) configPromise = (async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        try {
          const response = await win.fetch(labels.optionsUrl, { signal: controller.signal });
          if (!response.ok) throw new Error('Configuration unavailable');
          config = await response.json();
          return config;
        } finally { clearTimeout(timer); }
      })().catch((error) => { configPromise = null; throw error; });
      return configPromise;
    }

    function globalCountry(modal, actualCountry) {
      virtual?.remove();
      const code = actualCountry || modal.querySelector('#md-form__select__country')?.dataset.value || labels.currentCountry;
      const country = Array.from(modal.querySelectorAll('#orbeCountryList .md-form__select__country__list-link'))
        .find((node) => node.dataset.country === code && !node.dataset.xteinkGlobalRoute);
      if (!country || !/^[A-Z]{2}$/.test(code)) throw new Error('Actual country unavailable');
      const fallback = mappingsFor(config).find((option) => option.id === config.fallbackId);
      const origin = new URL(config.sites[fallback.site].origin);
      const node = country.cloneNode(true);
      // Keep the real shipping country. Only its site/locale route is virtual.
      Object.assign(node.dataset, { countryname: fallback.countryName, defaultlocale: fallback.languageCode,
        locales: fallback.languageCode, namelocales: fallback.languageName, subfoldersuffix: '',
        domainurl: origin.host, host: origin.host, primary: 'undefined',
        externalstore: '/', externalfullpath: 'true', externalsubdomain: '', primarymarket: 'false', xteinkGlobalRoute: 'true' });
      const name = node.querySelector('.md-form__select__span');
      if (name) name.textContent = fallback.countryName;
      virtual = doc.createElement('li');
      virtual.hidden = true;
      virtual.dataset.xteinkGlobalRouteRow = 'true';
      virtual.append(node);
      modal.querySelector('#orbeCountryList').append(virtual);
      return { node, countryCode: code, languageCode: fallback.languageCode };
    }

    function arm(choice) {
      clearTimeout(handoffTimer);
      writeHandoff(null); // A new explicit choice supersedes an unfinished transfer.
      handoffPending = false;
      selected = { choice, source: win.location.href };
      saveGlobal(choice.countryCode === null ? choice.actualCountry : null);
      if (choice.countryCode !== null) { virtual?.remove(); virtual = null; }
    }

    const finalizeRedirect = (url) => {
      const source = selected?.source || win.location.href;
      const isGlobal = selected?.choice.countryCode === null || (!selected && readGlobal());
      const target = isGlobal && config ? globalTarget(config, source) : preserveQuery(url, source);
      return config ? normalizeDestination(config, target, source) : target;
    };
    function bindRedirectPreparation() {
      let orbe;
      try { orbe = typeof orbito === 'undefined' ? win.orbito : orbito; } catch { return false; }
      if (!orbe) return false;
      if (redirectBinding?.orbe === orbe) return true;
      if (typeof orbe._prepareRedirectUrl !== 'function') return false;
      const original = orbe._prepareRedirectUrl;
      const wrapped = async function(url) {
        // Orbe 5.9.0 adds GA4 parameters after its public event. Finalize after that stage as well.
        return finalizeRedirect(await original.call(this, url));
      };
      orbe._prepareRedirectUrl = wrapped;
      const originalNavigate = orbe._navigateTo;
      let wrappedNavigate;
      if (typeof originalNavigate === 'function') {
        wrappedNavigate = async function(url) {
          if (handoffPending && !selected) { releaseNativeSubmission(); return; }
          if (!selected && readGlobal() && config && globalTarget(config, win.location.href) === win.location.href) {
            releaseNativeSubmission();
            return;
          }
          if (selected && config) {
            const finalUrl = finalizeRedirect(url);
            const handoff = createHandoff(config, selected.choice, finalUrl, selected.source);
            if (handoff) {
              const form = doc.getElementById('CountryLanguageHandoff');
              if (!form) throw new Error('Direct localization form unavailable');
              // Orbe has already saved the confirmed preference. Navigate by POST to the
              // destination, so Shopify applies localization before rendering the resource.
              form.action = new URL('/localization', config.sites[selected.choice.site].origin).href;
              form.querySelector('[name="country_code"]').value = handoff.country;
              form.querySelector('[name="language_code"]').value = handoff.language;
              form.querySelector('[name="return_to"]').value = handoff.returnTo;
              writeHandoff({ ...handoff, attempted: true, mode: 'direct' });
              labels.handoffMode = 'direct';
              form.submit();
              return;
            }
          }
          return originalNavigate.call(this, url);
        };
        orbe._navigateTo = wrappedNavigate;
      }
      const originalSubmit = orbe._submitLocalizationForm;
      let wrappedSubmit;
      if (typeof originalSubmit === 'function') {
        wrappedSubmit = async function(form) {
          // Orbe calls form.submit() directly; that bypasses the DOM submit event.
          cleanReturnTo(form);
          return originalSubmit.call(this, form);
        };
        orbe._submitLocalizationForm = wrappedSubmit;
      }
      redirectBinding = { orbe, original, wrapped, originalNavigate, wrappedNavigate, originalSubmit, wrappedSubmit };
      return true;
    }
    function releaseNativeSubmission() {
      let orbe;
      try { orbe = typeof orbito === 'undefined' ? win.orbito : orbito; } catch { return; }
      if (!orbe) return;
      const button = doc.getElementById('md-btn__form__onSubmit');
      // Orbe keeps this lock after a successful navigation. bfcache preserves both
      // the object and the disabled button, so the restored page needs a new submission.
      if (typeof orbe._releaseSubmitLock === 'function') orbe._releaseSubmitLock(button);
      else {
        orbe.isSubmitting = false;
        if (button) { button.disabled = false; button.removeAttribute?.('aria-disabled'); }
      }
    }
    const onRedirect = (event) => {
      if (event.detail?.url) event.detail.url = finalizeRedirect(event.detail.url);
    };
    function cleanReturnTo(form) {
      const source = selected?.source || win.location.href;
      const input = form.querySelector('input[name="return_to"]');
      if (!input) return;
      const target = config ? normalizeDestination(config, input.value || '/', source) : input.value || '/';
      const cleaned = new URL(preserveQuery(target, source));
      input.value = cleaned.origin === new URL(source).origin ? `${cleaned.pathname}${cleaned.search}${cleaned.hash}` : cleaned.href;
    }
    // Also support plugin versions which submit through the DOM event.
    const onSubmit = (event) => {
      if (event.target.id !== 'md-modal__form__id') return;
      win.queueMicrotask(() => cleanReturnTo(event.target));
    };
    const onNativeClick = (event) => {
      const button = event.target.closest?.('#md-btn__form__onSubmit');
      if (!button) return;
      if (handoffPending && !selected && !event.isTrusted) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (!selected && !event.isTrusted && !redirectBinding) {
        // Cached Orbe geolocation can auto-submit during its own initialization.
        // Hold that one submit until final URL/form hooks are installed.
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!automaticPending) {
          automaticPending = true;
          const generation = historyGeneration;
          ready().then(() => {
            if (!destroyed && !suspended && generation === historyGeneration && !selected && bindRedirectPreparation()) button.click();
          }).catch(() => {}).finally(() => {
            if (generation === historyGeneration) automaticPending = false;
          });
        }
        return;
      }
      if (!readGlobal() || selected) return;
      if (!event.isTrusted) {
        // A saved virtual site preference takes priority over Orbe's automatic country submit.
        event.preventDefault();
        event.stopImmediatePropagation();
      } else {
        saveGlobal(null);
        virtual?.remove();
        virtual = null;
        const modal = doc.getElementById('md-app-embed__modal');
        const current = modal?.querySelector('#md-form__select__country')?.dataset.value;
        const language = modal?.querySelector('#md-form__select__language')?.dataset.value;
        const country = Array.from(modal?.querySelectorAll('#orbeCountryList .md-form__select__country__list-link') || [])
          .find((node) => node.dataset.country === current);
        if (country && !country.dataset.locales.split(',').includes(language)) country.click();
      }
    };
    async function refreshLabels() {
      try {
        await ready();
        let countryCookie = doc.cookie.split('; ').find((cookie) => cookie.startsWith('mdApp_countryCodeDomain='))?.split('=')[1];
        let global = Boolean(readGlobal());
        let orbe;
        let remoteCountry;
        for (let attempt = 0; attempt < 80; attempt++) {
          try { orbe = typeof orbito === 'undefined' ? win.orbito : orbito; } catch { orbe = null; }
          if (countryCookie || global) break;
          if (typeof orbe?.getRemoteCountryCode === 'function') {
            const detected = await orbe.getRemoteCountryCode();
            // The public object exists before its asynchronous geolocation request finishes.
            // An empty result is pending, not an unconfigured (Global) country.
            if (/^[A-Z]{2}$/.test(detected || '')) { remoteCountry = detected; break; }
          }
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        if (!countryCookie && !global && remoteCountry) {
          global = !mappingsFor(config).some((option) => option.countryCode === remoteCountry);
          geoFallback = global;
        }
        countryCookie = doc.cookie.split('; ').find((cookie) => cookie.startsWith('mdApp_countryCodeDomain='))?.split('=')[1];
        global = Boolean(readGlobal()) || (!countryCookie && global);
        // Saved preferences guide Orbe navigation; Shopify localization describes the rendered page.
        const country = labels.currentCountry;
        doc.querySelectorAll('[data-country-language-label]').forEach((node) => {
          const text = labelFor(config, country, labels.currentLanguage, global, node.dataset.countryLanguageLabel, labels.currentLanguageName);
          if (node.textContent !== text) node.textContent = text;
        });
      } catch { /* Keep the server-rendered label when configuration is unavailable. */ }
    }

    win.addEventListener('orbe:before-redirect', onRedirect);
    doc.addEventListener('click', onNativeClick, true);
    doc.addEventListener('submit', onSubmit, true);
    doc.addEventListener('shopify:section:load', refreshLabels);
    function applyHandoff() {
      if (selected) { handoffPending = false; return false; }
      const handoff = validateHandoff(config, incoming, win.location.hostname);
      if (!handoff) { handoffPending = false; return false; }
      if (handoff.mode === 'direct') labels.handoffMode = 'direct';
      const form = doc.getElementById('CountryLanguageHandoff');
      const matched = labels.currentCountry === handoff.country && labels.currentLanguage === handoff.language;
      const finish = (state) => {
        writeHandoff(null);
        handoffPending = false;
        labels.handoffState = state;
        if (doc.documentElement) {
          doc.documentElement.dataset.countryLanguageHandoffState = state;
          doc.documentElement.dataset.countryLanguageHandoffMode = handoff.mode || 'relay';
        }
        if (state === 'failed') {
          const status = doc.querySelector('[data-country-language-status]');
          if (status) status.textContent = labels.errorText;
        }
      };
      if (matched || handoff.attempted) {
        finish(matched ? 'complete' : 'failed');
        return true;
      }
      if (!form || !form.dataset.countries?.split(',').includes(handoff.country) ||
          !form.dataset.languages?.split(',').includes(handoff.language)) {
        finish('failed');
        return true;
      }
      // This is Shopify's same-store localization form, not another cross-store redirect.
      form.querySelector('[name="country_code"]').value = handoff.country;
      form.querySelector('[name="language_code"]').value = handoff.language;
      form.querySelector('[name="return_to"]').value = handoff.returnTo;
      writeHandoff({ ...incoming, attempted: true }); // At most one POST, including failed responses.
      labels.handoffState = 'submitting';
      labels.handoffCountry = handoff.country;
      labels.handoffLanguage = handoff.language;
      form.submit();
      handoffTimer = setTimeout(() => { if (!destroyed) finish('failed'); }, 8000);
      return true;
    }
    // Restore virtual site routing before Orbe applies a saved country on returning visits.
    let bootstrapObserver;
    const restore = () => {
      const code = readGlobal();
      if (!code || !config || virtual) return Boolean(virtual) || !code;
      const modal = doc.getElementById('md-app-embed__modal');
      if (!modal) return false;
      try { globalCountry(modal, code); return true; } catch { return false; }
    };
    let attempts = 0;
    const bind = () => {
      if (!destroyed && !suspended && !bindRedirectPreparation() && ++attempts < 80) bindTimer = setTimeout(bind, 100);
    };
    const onPageHide = () => {
      suspended = true;
      historyGeneration++;
      selected = null;
      incoming = null;
      handoffPending = false;
      automaticPending = false;
      clearTimeout(bindTimer);
      clearTimeout(handoffTimer);
      bootstrapObserver?.disconnect();
      releaseNativeSubmission();
    };
    const onPageShow = (event) => {
      if (!event.persisted || destroyed) return;
      suspended = false;
      releaseNativeSubmission();
      attempts = 0;
      bind();
      virtual?.remove();
      virtual = null;
      ready().then(() => {
        if (destroyed || suspended) return;
        restore();
        void refreshLabels();
      }).catch(() => {});
    };
    win.addEventListener('pagehide', onPageHide);
    win.addEventListener('pageshow', onPageShow);
    bind();
    ready().then(() => {
      if (destroyed || suspended) return;
      if (incoming && applyHandoff()) { void refreshLabels(); return; }
      if (!restore()) {
        bootstrapObserver = new win.MutationObserver(() => { if (restore()) bootstrapObserver.disconnect(); });
        bootstrapObserver.observe(doc.body, { childList: true, subtree: true });
        setTimeout(() => bootstrapObserver.disconnect(), 8000);
      }
      void refreshLabels();
    }).catch(() => {
      handoffPending = false;
      if (incoming) {
        writeHandoff(null);
        labels.handoffState = 'failed';
        const status = doc.querySelector('[data-country-language-status]');
        if (status) status.textContent = labels.errorText;
      }
    });
    return { ready, arm, globalCountry, refreshLabels, bindRedirectPreparation, removeVirtualRoute() {
      virtual?.remove();
      virtual = null;
    }, isGlobal() {
      return Boolean(readGlobal()) || (geoFallback && !doc.cookie.split('; ').some((cookie) => cookie.startsWith('mdApp_countryCodeDomain=')));
    }, hasPreference() {
      return Boolean(readGlobal()) || doc.cookie.split('; ').some((cookie) => cookie.startsWith('mdApp_countryCodeDomain='));
    }, cancel() { selected = null; }, recoverSubmission() {
      // The custom view has reported a foreground failure and is offering native retry.
      selected = null;
      handoffPending = false;
      clearTimeout(handoffTimer);
      releaseNativeSubmission();
    },
      destroy() {
        destroyed = true;
        clearTimeout(bindTimer);
        clearTimeout(handoffTimer);
        bootstrapObserver?.disconnect();
        if (redirectBinding && redirectBinding.orbe._prepareRedirectUrl === redirectBinding.wrapped) redirectBinding.orbe._prepareRedirectUrl = redirectBinding.original;
        if (redirectBinding?.wrappedNavigate && redirectBinding.orbe._navigateTo === redirectBinding.wrappedNavigate) redirectBinding.orbe._navigateTo = redirectBinding.originalNavigate;
        if (redirectBinding?.wrappedSubmit && redirectBinding.orbe._submitLocalizationForm === redirectBinding.wrappedSubmit) redirectBinding.orbe._submitLocalizationForm = redirectBinding.originalSubmit;
        win.removeEventListener('orbe:before-redirect', onRedirect);
        win.removeEventListener('pagehide', onPageHide);
        win.removeEventListener('pageshow', onPageShow);
        doc.removeEventListener('click', onNativeClick, true);
        doc.removeEventListener('submit', onSubmit, true);
        doc.removeEventListener('shopify:section:load', refreshLabels);
      } };
  }

  const api = { mappingsFor, preserveQuery, globalTarget, labelFor, createHandoff, validateHandoff, createNavigation, normalizeDestination };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.XteinkCountryLanguageNavigation = createNavigation(window, document);
})();
